/**
 * Shared file browser for /admin/resources and /portal/resources.
 *
 * Props:
 *  - mode: "admin" | "read" (admin shows create/upload/delete; read shows download only)
 *
 * Maintains its own folder navigation state. On admin mutations, refreshes
 * the current folder contents.
 *
 * Download is performed by opening the cookie-protected URL in a new tab
 * (the backend sets Content-Disposition: attachment so the browser triggers
 * a save dialog).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Folder,
  FolderPlus,
  UploadSimple,
  House,
  CaretRight,
  DownloadSimple,
  PencilSimple,
  Trash,
  Plus,
} from "@phosphor-icons/react";
import {
  resourcesApi,
  resourcesAdminApi,
  fileKind,
  humanFileSize,
} from "../../api/resources";
import FileIcon from "./FileIcon";
import ConfirmDialog from "../courses/ConfirmDialog";

export default function ResourceBrowser({ mode = "read" }) {
  const { t } = useTranslation();
  const isAdmin = mode === "admin";
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [confirm, setConfirm] = useState(null); // {kind, id, label}
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (currentFolderId == null) {
        const data = await resourcesApi.listRoot();
        setFolders(data.folders);
        setFiles(data.files);
        setBreadcrumb([]);
      } else {
        const data = await resourcesApi.listFolder(currentFolderId);
        setFolders(data.folders);
        setFiles(data.files);
        setBreadcrumb(data.breadcrumb || []);
      }
    } finally {
      setLoading(false);
    }
  }, [currentFolderId]);

  useEffect(() => {
    load();
  }, [load]);

  const goRoot = () => setCurrentFolderId(null);
  const openFolder = (id) => setCurrentFolderId(id);

  const handleUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setUploading(true);
      try {
        await resourcesAdminApi.uploadFile(file, currentFolderId, null);
        await load();
      } catch (err) {
        console.error(err);
        alert(t("resources.upload_error"));
      } finally {
        setUploading(false);
      }
    },
    [currentFolderId, load, t],
  );

  const handleCreateFolder = useCallback(
    async (e) => {
      e?.preventDefault?.();
      const name = newFolderName.trim();
      if (!name) return;
      setCreating(true);
      try {
        await resourcesAdminApi.createFolder(name, currentFolderId);
        setNewFolderName("");
        await load();
      } finally {
        setCreating(false);
      }
    },
    [newFolderName, currentFolderId, load],
  );

  const handleDelete = useCallback(async () => {
    if (!confirm) return;
    if (confirm.kind === "folder") {
      await resourcesAdminApi.deleteFolder(confirm.id);
    } else {
      await resourcesAdminApi.deleteFile(confirm.id);
    }
    setConfirm(null);
    await load();
  }, [confirm, load]);

  const handleRename = useCallback(
    async (kind, id, currentName) => {
      const next = window.prompt(t("resources.rename_prompt"), currentName);
      if (!next || next.trim() === currentName) return;
      const trimmed = next.trim();
      if (kind === "folder") {
        await resourcesAdminApi.updateFolder(id, { name: trimmed });
      } else {
        await resourcesAdminApi.updateFile(id, { name: trimmed });
      }
      await load();
    },
    [t, load],
  );

  const triggerDownload = useCallback((file) => {
    // The endpoint is cookie-authenticated; opening in the same tab triggers
    // a download via Content-Disposition without leaving the SPA.
    const url = resourcesApi.downloadUrl(file.id);
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    a.click();
  }, []);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav
        data-testid="resources-breadcrumb"
        className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 flex-wrap"
      >
        <button
          type="button"
          onClick={goRoot}
          className="inline-flex items-center gap-1.5 hover:text-slate-900"
          data-testid="resources-crumb-root"
        >
          <House size={14} weight="bold" />
          {t("resources.root")}
        </button>
        {breadcrumb.map((b, i) => (
          <span key={b.id} className="inline-flex items-center gap-1">
            <CaretRight size={12} className="text-slate-300" />
            <button
              type="button"
              onClick={() => openFolder(b.id)}
              data-testid={`resources-crumb-${b.id}`}
              className={`${i === breadcrumb.length - 1 ? "text-slate-900" : "hover:text-slate-900"}`}
            >
              {b.name}
            </button>
          </span>
        ))}
      </nav>

      {/* Admin toolbar */}
      {isAdmin && (
        <div
          data-testid="resources-toolbar"
          className="flex flex-wrap items-center gap-3"
        >
          <form
            onSubmit={handleCreateFolder}
            className="flex items-center gap-2 flex-1 min-w-[280px] rounded-2xl border-2 border-dashed border-slate-200 bg-white px-4 py-2.5"
          >
            <FolderPlus size={16} weight="regular" className="text-slate-400" />
            <input
              type="text"
              data-testid="resources-new-folder-input"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t("resources.new_folder_placeholder")}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
            />
            <button
              type="submit"
              data-testid="resources-new-folder-submit"
              disabled={creating || !newFolderName.trim()}
              className="rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            >
              <Plus size={12} weight="bold" className="inline mr-1" />
              {t("resources.add_folder")}
            </button>
          </form>

          <button
            type="button"
            data-testid="resources-upload-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-xs font-medium text-white disabled:opacity-40"
          >
            <UploadSimple size={14} weight="bold" />
            {uploading ? t("resources.uploading") : t("resources.upload")}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            data-testid="resources-file-input"
          />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : folders.length === 0 && files.length === 0 ? (
        <p
          data-testid="resources-empty"
          className="text-sm text-slate-500 py-12 text-center"
        >
          {isAdmin ? t("resources.empty_admin") : t("resources.empty_read")}
        </p>
      ) : (
        <div className="space-y-2">
          {folders.map((f) => (
            <div
              key={f.id}
              data-testid={`resources-folder-${f.id}`}
              className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-900 transition-colors"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl adonis-gradient text-white">
                <Folder size={18} weight="fill" />
              </span>
              <button
                type="button"
                onClick={() => openFolder(f.id)}
                className="flex-1 text-left text-sm font-medium text-slate-900 hover:underline"
                data-testid={`resources-folder-open-${f.id}`}
              >
                {f.name}
              </button>
              {isAdmin && (
                <>
                  <button
                    type="button"
                    onClick={() => handleRename("folder", f.id, f.name)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                    aria-label="Renommer"
                    data-testid={`resources-folder-rename-${f.id}`}
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setConfirm({ kind: "folder", id: f.id, label: f.name })
                    }
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                    aria-label="Supprimer"
                    data-testid={`resources-folder-delete-${f.id}`}
                  >
                    <Trash size={16} />
                  </button>
                </>
              )}
            </div>
          ))}

          {files.map((file) => {
            const kind = fileKind(file.mime_type, file.original_filename);
            return (
              <div
                key={file.id}
                data-testid={`resources-file-${file.id}`}
                className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-900 transition-colors"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                  <FileIcon kind={kind} size={18} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {humanFileSize(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => triggerDownload(file)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                  data-testid={`resources-file-download-${file.id}`}
                >
                  <DownloadSimple size={14} weight="bold" />
                  {t("resources.download")}
                </button>
                {isAdmin && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleRename("file", file.id, file.name)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                      aria-label="Renommer"
                      data-testid={`resources-file-rename-${file.id}`}
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setConfirm({ kind: "file", id: file.id, label: file.name })
                      }
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                      aria-label="Supprimer"
                      data-testid={`resources-file-delete-${file.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={
          confirm?.kind === "folder"
            ? t("resources.delete_folder_title")
            : t("resources.delete_file_title")
        }
        description={
          confirm?.kind === "folder"
            ? t("resources.delete_folder_desc", { name: confirm?.label || "" })
            : t("resources.delete_file_desc", { name: confirm?.label || "" })
        }
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
